import { QRCodeCanvas } from 'qrcode.react';
import {
	Dialog, BackArrowIcon, Link, Input, Button, BanIcon,
	alephToast,
	NotificationToast,
	NotificationType,
	LoaderBars,
	isReddit,
	CrossRightIcon,
} from '@aleph/shared-components';
import GoogleAuthenticatorDiagram from 'packages/wb-reddit/src/app/common/components/googleAuthenticator.svg';
import useAccessTokenStore from 'auth/lib/useAccessTokenStore';
import {
	Dispatch, SetStateAction, useEffect, useState, useCallback, useMemo,
} from 'react';
import { refreshTokenWrapper } from 'wb-reddit/app/common/components/utils';
import { DeepPartial } from '@apollo/client/utilities';
import TwoFactorHeader from 'wb-reddit/app/common/components/twoFactor/twoFactorModalHeader';
import TwoFactorCodeSkeleton from 'wb-reddit/app/common/components/twoFactor/twoFactorSkeleton';
import classNames from 'classnames';
import { TwoFactorStepType, TwoFactorSteps } from 'wb-reddit/app/common/components/twoFactor/twoFactorModal';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import { useTranslation } from 'react-i18next';
import { captureException } from '@sentry/react';

export interface TwoFactorStepProps {
	type: TwoFactorStepType
	onClose: () => void
	setRecoveryCodes: Dispatch<SetStateAction<string[]>>
	recoveryCodes: string[]
	setStep: Dispatch<SetStateAction<TwoFactorSteps>>
	email: string
}

type GetQRCodeResponse = DeepPartial<{
	data: {
		url: string
		message: string
	}
	metadata: unknown[]
	status: string
}>;

type EnableResponse = DeepPartial<{
	data: {
		message: string
		recoveryCodes: string[]
	}
	metadata: unknown[]
	status: string
}>;

export default function TwoFactorAuthCodeStep({
	type, setRecoveryCodes, setStep, onClose,
}: TwoFactorStepProps) {
	const { t } = useTranslation();

	const [url, setUrl] = useState('');
	const [authenticatorCode, setAuthenticatorCode] = useState('');
	const [loadingQRCode, setLoadingQRCode] = useState(false);
	const [loadingAction, setLoadingAction] = useState(false);
	const [errorAction, setErrorAction] = useState('');
	const accessToken = useAccessTokenStore(state => state.accessToken);
	const platformUser = useUserMetaData();
	const network = useMemo(() => (isReddit() ? 'pn_re' : 'gn_ga'), []);

	const getQRUrl = async () => {
		setLoadingQRCode(true);

		try {
			const data = await refreshTokenWrapper(
				(newAccessToken) => fetch(
					`${import.meta.env.VITE_API_PATH}/two-factor-authentication/${network}/get-url`,
					{
						method:  'GET',
						headers: {
							authorization: `Bearer ${newAccessToken ?? accessToken}`,
						},
					},
				),
			) as GetQRCodeResponse;

			if (data.status === 'success') {
				setUrl(data.data?.url ?? '');
			}

			if (data.status === 'error') {
				throw Error(data.data?.message);
			}// sc: auth.tfa.
		} catch (err) {
			captureException(err);

			alephToast(
				<NotificationToast
					type={NotificationType.Error}
					title={
						(err as Error)?.message
							?? t('sc: auth.tfa.code_loading_err', 'An error has occurred while loading the QR code')
					}
				/>,
			);
		}
		setLoadingQRCode(false);
	};

	useEffect(() => {
		if (type === 'enable') {
			getQRUrl();
		}
	}, []);

	const changeTwoFactor = useCallback(async () => {
		setLoadingAction(true);

		try {
			const data = await refreshTokenWrapper(
				(newAccessToken) => fetch(
					`${import.meta.env.VITE_API_PATH}/two-factor-authentication/${network}/${type}`,
					{
						method:  'POST',
						headers: {
							authorization: `Bearer ${newAccessToken ?? accessToken ?? null}`,
						},
						body: JSON.stringify({ code: authenticatorCode }),
					},
				),
			) as EnableResponse;

			if (data.status === 'success') {
				setStep('success');

				if (type === 'enable') {
					setRecoveryCodes(data.data?.recoveryCodes as string[] ?? []);
				}

				await platformUser?.refetch();
			}

			if (data.status === 'error' && data.data?.message === 'Invalid code.') {
				setErrorAction(t('sc: auth.tfa.invalid_code', 'The code does not match. Make sure it’s for the correct service and user account.'));
			}
		} catch (err) {
			captureException(err);

			alephToast(
				<NotificationToast
					type={NotificationType.Error}
					title={
						t('sc: auth.tfa.submit_err', 'An error has occurred while enabling two factor authentication')
					}
				/>,
			);
		}
		setLoadingAction(false);
	}, [accessToken, authenticatorCode, type]);

	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Enter') {
				changeTwoFactor();
			}
		};
		window.addEventListener('keydown', onKeyDown);

		return () => {
			window.removeEventListener('keydown', onKeyDown);
		};
	}, [changeTwoFactor]);

	if (loadingQRCode) {
		return (<TwoFactorCodeSkeleton
			type={type}
			onClose={onClose}
		/>);
	}

	return (
		<>
			<TwoFactorHeader
				type={type}
				onClose={onClose}
			/>
			<Dialog.Content>
				<div className="flex flex-col gap-8 min-w-[584px]">
					<div className='flex flex-col gap-4'>
						<p className='text-base font-semibold'>
							{type === 'enable'
								? t('sc: auth.tfa.scan', 'Scan the QR code on your phone')
								: t('sc: auth.tfa.open_google', 'Open Google Authenticator app')}
						</p>
						{
							type === 'enable'
								? <p className='text-[13px] !m-0'>
									You need the{' '}
									<Link
										className='!inline'
										variant='external'
										href='https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en&gl=US'
										hideExternalIcon
									>
										Google Authenticator app
									</Link> installed to complete the set-up</p>
								: <p className='text-[13px] !m-0'>{t('sc: auth.tfa.requires_auth', 'The setting change requires authorization.')}</p>
						}
						<div className={classNames(
							'flex flex-row items-center',
							{ 'justify-center': type === 'disable' },
						)}
						>
							{
								type === 'enable'
									? <>
										<div className='px-8'>
											<QRCodeCanvas
												width={164}
												height={164}
												size={164}
												style={{
													height: 164,
													width:  164,
												}}
												value={url}
											/>
										</div>
										<CrossRightIcon
											width={64}
											height={64}
											fill="var(--gray-400)"
										/>
									</> : null
							}
							<div className={type === 'enable' ? 'px-[11px]' : undefined}>
								<img
									src={GoogleAuthenticatorDiagram}
									style={{ marginLeft: 11 }}
									alt="Google Authenticator diagram"
								/>
							</div>
						</div>
					</div>
					<div className='flex flex-col gap-4 mb-4'>
						<p className='text-base font-semibold'>
							{t('sc: auth.tfa.enter_auth_code', 'c')}
						</p>
						<div className='flex flex-col'>
							<Input
								label={t('', 'Authentication code')}
								value={authenticatorCode}
								onChange={(e) => {
									setAuthenticatorCode(e.target.value);
								}}
								validationError={errorAction}
								autoFocus
								instructionText={t('sc: auth.tfa.code_google', 'Type the code from the Google Authenticator app on your phone')}
							/>
						</div>
					</div>
				</div>
			</Dialog.Content>
			<Dialog.Footer>
				<Button
					title={t('sc: cancel', 'Cancel')}
					leftNode={<BanIcon />}
					appearance="outlined"
					color="gray"
					onClick={onClose}
				/>
				<Button
					title={t('sc: confirm', 'Confirm')}
					leftNode={loadingAction ? <LoaderBars
						className="animate-spin"
						width={16}
						height={16}
					/> : <BackArrowIcon />}
					onClick={changeTwoFactor}
					disabled={!authenticatorCode}
					loading={loadingAction}
				/>
			</Dialog.Footer>
		</>
	);
}
